import { isEmpty, without } from 'lodash'
import Mousetrap from 'mousetrap'
import PropTypes from 'prop-types'
import { PureComponent } from 'react'
import { Helmet } from 'react-helmet'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { webFetch } from 'shared/api'
import { receivedGifs } from 'shared/redux/gifs'

import BackdropAd from 'shared/components/ads/giphy-backdrop'
import Grid from 'shared/components/grid'

import Tags from 'shared/components/tags/tags'
import gifProps from 'shared/prop-types/gif'
import { toggleEditMode, updateEditMode } from 'shared/redux/edit-mode'
import { getEditModeIsEditing } from 'shared/redux/reducers'
import { getUrl } from 'shared/util/gif-extract'
import { getTitle } from 'shared/util/gif-text'
import { grid9 } from 'shared/util/grid'
import Attribution from '../containers/attribution'
import css from './detail.css'
import DownloadBackdrop from './download-backdrop'
import Gif from './gif'
import AttributionGrid from 'shared/components/sticker-attribution/attribution-grid.tsx'
/**
 * GIF Detail: Controls the main GIF Detail component
 * and layout.
 *
 * @class GifDetail
 */
@withRouter
@connect(
    state => ({
        isEditing: getEditModeIsEditing(state),
    }),
    { updateEditMode, toggleEditMode, receivedGifs }
)
export default class Detail extends PureComponent {
    state = {
        gridType: 'related',
        hasRelated: false,
    }
    static propTypes = {
        gif: gifProps.isRequired,
        prevGif: gifProps,
        nextGif: gifProps,
        permissions: PropTypes.object,
        isEditOn: PropTypes.bool,
        isBackdrop: PropTypes.bool,
    }
    updateDocumentTitle(gif) {
        this.setState({
            documentTitle: gif
                ? gif.is_sticker
                    ? `${getTitle(gif)} for iOS & Android | GIPHY`
                    : `${getTitle(gif)} - Find & Share on GIPHY`
                : 'Find & Share on GIPHY',
        })
    }
    setCorrectGridType(gif) {
        if (gif && gif.has_attribution) {
            this.setState({ gridType: 'stickerAttribution' })
        }
    }

    componentWillMount() {
        const { gif } = this.props
        this.setCorrectGridType(gif)
        this.hasRelatedGifs()
        this.updateDocumentTitle(gif)
    }

    hasRelatedGifs = () => {
        this.fetchGifs().then(result => {
            if (result.data.length) {
                this.setState({ hasRelated: true })
            }
        })
    }

    componentWillReceiveProps(nextProps) {
        if (this.props.gif.id !== nextProps.gif.id) {
            this.setState({ hasRelated: false })
            if (nextProps.gif.has_attribution) {
                this.setState({ gridType: 'stickerAttribution' })
                this.hasRelatedGifs()
            } else {
                this.setState({ gridType: 'related' })
            }
            this.updateDocumentTitle(nextProps.gif)
        }
    }

    componentDidMount() {
        // activate fullscreen mode
        Mousetrap.bind('up', () => {
            const {
                gif: { path },
                history: { replace },
            } = this.props
            replace(`${path}/fullscreen`)
            return false
        })

        // activate tile mode
        Mousetrap.bind('shift+up', () => {
            const {
                gif: { path },
                history: { replace },
            } = this.props
            replace(`${path}/tile`)
            return false
        })

        // reset takeover
        Mousetrap.bind(['down', 'shift+down', 'esc'], () => {
            this.resetTakeoverMode()
            return false
        })

        // reset takeover (esc)
        Mousetrap.bind('esc', () => {
            this.resetTakeoverMode()
            return false
        })

        // prev gif
        Mousetrap.bind('left', () => {
            const {
                prevGif,
                history: { push },
                match: {
                    params: { mode = '' },
                },
            } = this.props
            prevGif && push(`${prevGif.path}/${mode}`)
            return false
        })

        // next gif
        Mousetrap.bind('right', () => {
            const {
                nextGif,
                history: { push },
                match: {
                    params: { mode = '' },
                },
            } = this.props
            nextGif && push(`${nextGif.path}/${mode}`)
            return false
        })

        // edit mode
        Mousetrap.bind('command+i', () => {
            const {
                gif: { path },
                permissions: { edit },
                history: { replace },
            } = this.props
            edit && replace(`${path}/edit`)
            return false
        })

        this.props.mode === 'edit' && this.openEditPanel()
    }
    componentDidUpdate({ isEditing: prevIsEditing, mode: prevMode }) {
        const { mode, isEditing } = this.props
        const isEdit = mode === 'edit'

        if (isEdit && mode !== prevMode) {
            this.openEditPanel()
        }
        if (!isEditing && isEditing !== prevIsEditing) {
            this.resetTakeoverMode()
        }
    }
    componentWillUnmount() {
        this.historyListener && this.historyListener()
        Mousetrap.unbind(['up', 'down', 'left', 'right', 'shift+up', 'shift+down', 'esc'])
    }

    openEditPanel() {
        const { gif, updateEditMode, toggleEditMode } = this.props
        updateEditMode(gif)
        toggleEditMode(true)
    }

    resetTakeoverMode() {
        const {
            gif: { path },
            history: { replace },
            match: {
                params: { mode },
            },
        } = this.props
        mode && replace(path)
    }

    handleGridSwap = type => {
        const { gridType } = this.state
        if (type === gridType) return
        this.setState({ gridType: type })
    }

    fetchGifs = offset => webFetch.related(this.props.gif.id, { offset })

    render() {
        const {
            gif,
            match: {
                params: { mode = '' },
            },
            isBackdrop,
        } = this.props
        const { gridType, documentTitle, hasRelated } = this.state
        const { is_sticker, tags, has_attribution } = gif
        const sanitizedTags = without(tags, 'transparent')
        return (
            <div className={css.container}>
                <Helmet>
                    <title>{documentTitle}</title>
                </Helmet>
                <div className={css.sidebar}>
                    <Attribution isCompact={true} gif={gif} />
                    <BackdropAd />
                </div>
                <div className={css.content}>
                    <Gif gifId={gif.id}>{isBackdrop && <DownloadBackdrop gif={gif} />}</Gif>
                    <div>
                        {!isEmpty(sanitizedTags) && (
                            <Tags className={css.gifTags} tags={sanitizedTags} isSticker={is_sticker} />
                        )}
                        {has_attribution && (
                            <div className={css.buttonContainer}>
                                <p
                                    onClick={() => this.handleGridSwap('stickerAttribution')}
                                    className={
                                        gridType === 'stickerAttribution' ? css.activeGridButton : css.gridTypeButtons
                                    }
                                >
                                    Stickers Used
                                </p>
                                {hasRelated && (
                                    <p
                                        onClick={() => this.handleGridSwap('related')}
                                        className={
                                            gridType !== 'stickerAttribution'
                                                ? css.activeGridButton
                                                : css.gridTypeButtons
                                        }
                                    >
                                        Related Gifs
                                    </p>
                                )}
                            </div>
                        )}

                        {gridType === 'stickerAttribution' && (
                            <AttributionGrid
                                key={`${gif.id}-${gridType}`}
                                overrideWidth={grid9}
                                columns={3}
                                gif={gif}
                            />
                        )}
                        {gridType === 'related' && (
                            <Grid key={`${gif.id}-${gridType}`} fetchGifs={this.fetchGifs} width={grid9} />
                        )}
                    </div>
                </div>
                {mode === 'tile' && (
                    <div
                        className={css.takeover}
                        data-sticker={is_sticker}
                        title="Back to GIF"
                        style={{ backgroundImage: `url(${getUrl(gif)})` }}
                        onClick={() => this.resetTakeoverMode()}
                    />
                )}
            </div>
        )
    }
}
